import React from 'react';

// Colors matching actual Ergosource product finishes
export const DEFAULT_COLORS = {
    // Aluminum/silver leg finish
    aluminum: '#c0c0c0',
    aluminumLight: '#d8d8d8',
    aluminumDark: '#a0a0a0',
    aluminumOutline: '#808080',
    // Frame/crossbar finish
    frame: '#888888',
    frameOutline: '#666666',
    // Leveling glides
    glide: '#2a2a2a',
    glideOutline: '#1a1a1a',
    // Surface
    surface: '#e8e8e8',
    surfaceOutline: '#b0b0b0',
    // Accessories
    accessory: '#4a90e2',
    accessoryOutline: '#357abd'
};

export const TableSurface = ({ width, depth, colors }) => {
    const C = colors || DEFAULT_COLORS;
    return (
        <g id="surface">
            <rect
                x={-width / 2}
                y={-depth / 2}
                width={width}
                height={depth}
                fill={C.surface}
                stroke={C.surfaceOutline}
                strokeWidth="1"
            />
            {/* Specular highlight hint */}
            <path
                d={`M${-width / 2} ${-depth / 2} L${-width / 2 + 5} ${-depth / 2 + 5}`}
                stroke={C.surfaceOutline}
                strokeWidth="0.5"
            />
        </g>
    );
};

// 2-Leg Frame - Corrected Orientation (Front-to-Back foot)
export const LegTwoPost = ({ height, tableDepth, colors }) => {
    const C = colors || DEFAULT_COLORS;
    const postWidth = 16;
    const visualFootWidth = 24; // Visual width from front view (foot goes into depth)
    const segmentGap = 2;

    return (
        <g>
            {/* ===== Top Bracket/Arm (Front View) ===== */}
            <g transform="translate(0, -2)">
                <rect
                    x={-visualFootWidth / 2}
                    y={-6}
                    width={visualFootWidth}
                    height={8}
                    fill={C.frame}
                    stroke={C.frameOutline}
                    strokeWidth="1"
                />
            </g>

            {/* ===== MAIN VERTICAL LEG COLUMN ===== */}
            {/* Upper/Outer segment */}
            <g transform="translate(0, 0)">
                <rect
                    x={-postWidth / 2}
                    y={0}
                    width={postWidth}
                    height={height * 0.55}
                    fill={C.aluminum}
                    stroke={C.aluminumOutline}
                    strokeWidth="1"
                />
                {/* T-slot grooves */}
                <line x1={-postWidth / 2 + 4} y1={0} x2={-postWidth / 2 + 4} y2={height * 0.55} stroke={C.aluminumDark} strokeWidth="1" />
                <line x1={postWidth / 2 - 4} y1={0} x2={postWidth / 2 - 4} y2={height * 0.55} stroke={C.aluminumDark} strokeWidth="1" />
            </g>

            {/* Lower/Inner segment */}
            <g transform={`translate(0, ${height * 0.55 + segmentGap})`}>
                <rect
                    x={-postWidth / 2 + 2}
                    y={0}
                    width={postWidth - 4}
                    height={height * 0.45 - segmentGap - 8}
                    fill={C.aluminumLight}
                    stroke={C.aluminumOutline}
                    strokeWidth="1"
                />
                {/* Grooves */}
                <line x1={0} y1={0} x2={0} y2={height * 0.45 - segmentGap - 8} stroke={C.aluminumDark} strokeWidth="0.5" />
            </g>

            {/* ===== FOOT (Front View) ===== */}
            <g transform={`translate(0, ${height})`}>
                {/* Visual representation of foot facing forward/back */}
                {/* We see the front face of the foot/end cap */}
                <rect
                    x={-visualFootWidth / 2}
                    y={-8}
                    width={visualFootWidth}
                    height={10}
                    fill={C.frame}
                    stroke={C.frameOutline}
                    strokeWidth="1"
                />

                {/* Perspective Hint: Top surface of foot receding back */}
                <path
                    d={`M${-visualFootWidth / 2} -8 L${-visualFootWidth / 2 + 2} -12 L${visualFootWidth / 2 - 2} -12 L${visualFootWidth / 2} -8`}
                    fill={C.frame}
                    opacity="0.5"
                    stroke="none"
                />

                {/* Leveling Glide (Front) */}
                <circle cx={0} cy={6} r={5} fill={C.glide} stroke={C.glideOutline} strokeWidth="1" />
                <rect x={-3} y={2} width={6} height={4} fill={C.aluminumDark} />
            </g>
        </g>
    );
};

// 4-Leg Levitech Series
export const LegFourPost = ({ height, colors }) => {
    const C = colors || DEFAULT_COLORS;
    const postWidth = 12;
    const innerPostWidth = 10;
    const segmentGap = 1;

    return (
        <g>
            {/* Top mounting plate */}
            <rect
                x={-postWidth / 2 - 3}
                y={-4}
                width={postWidth + 6}
                height={6}
                fill={C.frame}
                stroke={C.frameOutline}
                strokeWidth="1"
            />

            {/* Upper/Outer segment */}
            <g transform="translate(0, 2)">
                <rect
                    x={-postWidth / 2}
                    y={0}
                    width={postWidth}
                    height={height * 0.5}
                    fill={C.aluminum}
                    stroke={C.aluminumOutline}
                    strokeWidth="1"
                />
                <circle cx={0} cy={height * 0.15} r={1.5} fill={C.aluminumDark} />
                <circle cx={0} cy={height * 0.35} r={1.5} fill={C.aluminumDark} />
            </g>

            {/* Lower/Inner segment */}
            <g transform={`translate(0, ${2 + height * 0.5 + segmentGap})`}>
                <rect
                    x={-innerPostWidth / 2}
                    y={0}
                    width={innerPostWidth}
                    height={height * 0.5 - segmentGap - 10}
                    fill={C.aluminumLight}
                    stroke={C.aluminumOutline}
                    strokeWidth="1"
                />
            </g>

            {/* Circular leveling glide at bottom */}
            <g transform={`translate(0, ${height - 6})`}>
                <rect
                    x={-innerPostWidth / 2 - 1}
                    y={0}
                    width={innerPostWidth + 2}
                    height={4}
                    fill={C.aluminumDark}
                    stroke={C.aluminumOutline}
                    strokeWidth="0.5"
                />
                <circle
                    cx={0}
                    cy={8}
                    r={6}
                    fill={C.glide}
                    stroke={C.glideOutline}
                    strokeWidth="1"
                />
            </g>
        </g>
    );
};

export const FrameBeamHorizontal = ({ width, colors }) => {
    const C = colors || DEFAULT_COLORS;
    return (
        <rect
            x={-width / 2}
            y={-3}
            width={width}
            height={6}
            fill={C.frame}
            stroke={C.frameOutline}
            strokeWidth="1"
        />
    );
};

export const AccessoryDrawer = ({ x, y, mountingHeight, colors }) => {
    const C = colors || DEFAULT_COLORS;
    return (
        <g transform={`translate(${x}, ${y})`}>
            <rect x="0" y="0" width="15" height="20" fill={C.accessory} stroke={C.accessoryOutline} />
            <line x1="4" y1="4" x2="11" y2="4" stroke="white" strokeWidth="1" />
            <line x1="4" y1="10" x2="11" y2="10" stroke="white" strokeWidth="1" />
            <line x1="7.5" y1="-5" x2="7.5" y2="0" stroke={C.frameOutline} strokeWidth="1" />
        </g>
    );
};

export const AccessoryPowerStrip = ({ width }) => (
    <g>
        <rect x={-width / 2 + 2} y="-2" width={width - 4} height="4" fill="#333" rx="1" />
        <circle cx="0" cy="0" r="1" fill="red" />
    </g>
);

export const AccessoryMonitorArm = ({ x }) => (
    <g transform={`translate(${x}, -5)`}>
        <path d="M0,0 L0,-10 L5,-15" fill="none" stroke="#333" strokeWidth="1.5" />
        <rect x="4" y="-18" width="8" height="6" fill="#333" />
    </g>
);
